import React, { useMemo } from 'react';
import PointsMap, { type MapMarker } from './PointsMap';

/**
 * FriendsMap — shows the user's friends as colored pins on a map.
 *
 * Color encodes `visitOnNextJourney`:
 *   YES   → green   (definitely visiting)
 *   MAYBE → yellow  (visit if it's on the way)
 *   NO    → red     (not visiting)
 *
 * Friends without coordinates are skipped silently. When no friend has
 * coordinates, the map renders with an empty-state overlay.
 */

export type FriendVisit = 'YES' | 'MAYBE' | 'NO';

export interface MappableFriend {
  id: string;
  name: string;
  // Backend stores these as Prisma Decimal, which serializes as a string;
  // accept both forms and coerce in the component.
  latitude?: number | string | null;
  longitude?: number | string | null;
  visitOnNextJourney: FriendVisit;
}

const toFiniteNumber = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

interface FriendsMapProps {
  friends: MappableFriend[];
  height?: number;
}

const VISIT_COLOR: Record<FriendVisit, string> = {
  YES: '#10b981',
  MAYBE: '#eab308',
  NO: '#ef4444',
};

const firstName = (name: string): string => name.trim().split(/\s+/)[0] || name;

const FriendsMap: React.FC<FriendsMapProps> = ({ friends, height = 240 }) => {
  const markers = useMemo<MapMarker[]>(
    () =>
      friends
        .map((f): MapMarker | null => {
          const lat = toFiniteNumber(f.latitude);
          const lng = toFiniteNumber(f.longitude);
          if (lat === null || lng === null) return null;
          return {
            id: f.id,
            lat,
            lng,
            label: firstName(f.name),
            color: VISIT_COLOR[f.visitOnNextJourney],
          };
        })
        .filter((m): m is MapMarker => m !== null),
    [friends]
  );

  const overlay =
    markers.length === 0 ? (
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.92)',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          padding: '12px 16px',
          fontSize: 14,
          color: '#334155',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          maxWidth: '80%',
          textAlign: 'center',
        }}
      >
        Add friends to see them on the map.
      </div>
    ) : undefined;

  return <PointsMap markers={markers} height={height} overlay={overlay} />;
};

export default FriendsMap;
